import React, { useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import useFormKey from '@/components/ContentEditor/hooks/useFormKey';
import { PageError } from '@/components/Error';
import Loader from '@/components/Loader';
import useBodyHeightStyles from '@/hooks/useBodyHeightStyles';
import useSearchParam from '@/hooks/useSearchParam';
import Design from '@/modules/corporate/components/Design';
import ErrorAccess from '@/modules/corporate/components/ErrorAccess';
import { useEmbeddedQuizEditor } from '@/modules/corporate/modules/course/components/QuizCardContainer/components/QuizEditorModal/context';
import { MediaKindEnum, QuestionApplicableOnEnum, UpdateQuestionEntity } from '@/types/schema';

import LimitedEditingModal from './components/LimitedEditingModal';
import QuestionEditor from './components/QuestionEditor';
import Header from './components/QuizEditorHeader';
import Sidebar from './components/QuizEditorSidebar';
import { QuizEditorContextProvider } from './context/QuizEditor';
import useQuizEditor from './hooks/useQuizEditor';
import { QuizCreatorActionsEnum } from './hooks/useQuizEditor/actions';
import useQuizEditorData from './hooks/useQuizEditorData';
import { QuizEditorContentProps, QuizEditorProps } from './types';
import { getIsQuestionImported } from './utils';

export const TAB_QUERY_KEY = 'tab';

function QuizEditorContent(props: QuizEditorContentProps) {
  const tab = useSearchParam(TAB_QUERY_KEY);
  const { t } = useTranslation();
  const { quiz, refetchQuiz } = props;
  const embeddedEditor = useEmbeddedQuizEditor();
  const {
    saving,
    questions,
    isQuizPublished,
    canEditSections,
    onAction,
    uiState,
    onCloseLimitedEditingModal,
    onClosePublishChangesDialog,
    onPublishSubmit,
    onPublish,
    onPreview,
    setNudgeState,
    publishedQuestionById,
  } = useQuizEditor({ quiz });
  const [open, { toggle }] = useToggle(true);
  const { bodyMaxHeightStyles } = useBodyHeightStyles();

  const { formKey, onRefresh: onRefetch } = useFormKey(quiz);

  const { isLocalizedContent } = quiz;
  const onSubmitCover = useCallback(
    (url: string, name: string) => {
      onAction({
        type: QuizCreatorActionsEnum.UpdateQuiz,
        payload: {
          quiz: { cover: { name, url, kind: MediaKindEnum.Image } },
          snackbarProps: { message: t('QuizCreator:Design.CoverImageHasBeenChanged') },
        },
      });
      return Promise.resolve(true);
    },
    [onAction, t],
  );

  const skillIds = useMemo(() => {
    return quiz.skills?.map((it) => it.id);
  }, [quiz.skills]);

  const onUpdateQuestion = useCallback(
    (updatedQuestion: UpdateQuestionEntity) => {
      onAction({
        type: QuizCreatorActionsEnum.UpdateQuestion,
        payload: { question: updatedQuestion },
      });
    },
    [onAction],
  );

  if (!uiState.isOfSameWorkspace) {
    return <ErrorAccess resource={t('Common:ResourceTypeSM.Quiz')} />;
  }

  const renderSelectedQuestion = () => {
    const question = questions[uiState.selectedQuestionIndex];

    if (!question) {
      return;
    }

    const allowPartialUpdates =
      isQuizPublished || getIsQuestionImported(quiz, questions[uiState.selectedQuestionIndex].id);

    return (
      <QuestionEditor
        key={`${question.id}_${question.questionType}_${question.subType}_${formKey}`}
        applicableOn={QuestionApplicableOnEnum.Quiz}
        error={uiState.errors[question.id]}
        index={uiState.selectedQuestionIndex}
        initialQuestion={publishedQuestionById[question.id]}
        isLocalizedContent={isLocalizedContent}
        languageType={quiz.languageType ?? undefined}
        limitedEditingInfoState={{ open, toggle }}
        question={question}
        quizSkillIds={skillIds}
        setNudgeState={setNudgeState}
        viewMode={allowPartialUpdates ? 'partialUpdate' : 'create'}
        onUpdateQuestion={onUpdateQuestion}
      />
    );
  };

  return (
    <QuizEditorContextProvider
      canEditSections={canEditSections}
      isQuizPublished={isQuizPublished}
      publishedQuestionById={publishedQuestionById}
      questions={questions}
      quiz={quiz}
      refetchQuiz={refetchQuiz}
      uiState={uiState}
      onAction={onAction}>
      <Header
        quiz={quiz}
        refetchQuiz={refetchQuiz}
        saving={Boolean(saving || uiState.isPublishing)}
        showPublishPopover={uiState.showPublishChangesDialog}
        onAction={onAction}
        onClose={onClosePublishChangesDialog}
        onPreview={onPreview}
        onPublish={onPublish}
        onPublishSubmit={onPublishSubmit}
        onRevertSuccess={onRefetch}
      />
      <Stack
        className={cn('flex-1', embeddedEditor ? 'min-h-0 overflow-hidden' : bodyMaxHeightStyles)}>
        <Design
          contentName={quiz.name}
          currentCoverUrl={quiz.cover?.url ?? ''}
          onSubmitCover={onSubmitCover}>
          <Sidebar tab={tab} />
          <Stack className="relative flex-1">
            <Design.Preview className="absolute z-20 h-full w-full bg-bg-primary" />
            {renderSelectedQuestion()}
          </Stack>
        </Design>
      </Stack>
      {uiState.showLimitingEditingModal ? (
        <LimitedEditingModal.Quiz onClose={onCloseLimitedEditingModal} />
      ) : null}
    </QuizEditorContextProvider>
  );
}

const QuizEditor: React.FC<QuizEditorProps> = (props) => {
  const { contentId } = props;
  const { t } = useTranslation();

  const { fetching, error, quiz, refetchQuiz } = useQuizEditorData({
    contentId,
  });

  if (fetching) {
    return <Loader />;
  }

  if (error) {
    return <PageError error={error} />;
  }

  if (!quiz || quiz.sharedByWorkspace) {
    return <ErrorAccess resource={t('Common:ResourceTypeSM.Quiz')} />;
  }

  return <QuizEditorContent quiz={quiz} refetchQuiz={refetchQuiz} />;
};

export default QuizEditor;
